import { FunctionComponent, KeyboardEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faShieldCheck, faShieldXmark } from "@fortawesome/pro-regular-svg-icons";

import { JobTitleScreeningItem, JobTitleVerdict } from "@signalist/shared-types";

import { Icon } from "@/components/Icon/Icon";
import { classNames } from "@/utils/classNames";

import { JobTitleRow } from "./JobTitleRow";

interface JobTitleColumnProps {
	verdict: JobTitleVerdict;
	count: number;
	rows: JobTitleScreeningItem[];
	selectedIds: number[];
	/** The other column owns the current selection — lock this one. */
	isLocked: boolean;
	onToggleSelect: (id: number, column: JobTitleVerdict) => void;
	onMoveOne: (id: number, from: JobTitleVerdict) => void;
	onDelete: (id: number) => void;
	onAdd: (title: string, verdict: JobTitleVerdict) => void;
}

export const JobTitleColumn: FunctionComponent<JobTitleColumnProps> = ({
	verdict,
	count,
	rows,
	selectedIds,
	isLocked,
	onToggleSelect,
	onMoveOne,
	onDelete,
	onAdd,
}) => {
	const { t } = useTranslation();
	const [draft, setDraft] = useState("");

	const isWhitelist = verdict === JobTitleVerdict.WHITELISTED;
	const headerLabel = t(
		isWhitelist ? "leadScoring.jobTitles.whitelistColumn" : "leadScoring.jobTitles.excludedColumn"
	);
	const moveLabel = t(
		isWhitelist ? "leadScoring.jobTitles.exclude" : "leadScoring.jobTitles.whitelist"
	);

	const submitDraft = () => {
		const value = draft.trim();

		if (value) {
			onAdd(value, verdict);
			setDraft("");
		}
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			event.preventDefault();
			submitDraft();
		}
	};

	return (
		<div
			className={classNames(
				"flex min-h-0 flex-1 flex-col rounded-xl border border-gray-200 bg-white",
				isLocked && "pointer-events-none opacity-50"
			)}
			data-test={`job-title-column-${verdict}`}
		>
			<div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
				<span
					className={classNames(
						"flex h-7 w-7 items-center justify-center rounded-lg",
						isWhitelist
							? "bg-status-success-light text-status-success"
							: "bg-status-error-light text-status-error"
					)}
				>
					<Icon className="h-3.5 w-3.5" icon={isWhitelist ? faShieldCheck : faShieldXmark} />
				</span>
				<span className="flex-1 text-sm font-semibold text-text">{headerLabel}</span>
				<span
					className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-semibold text-text-meta"
					data-test={`job-title-count-${verdict}`}
				>
					{count}
				</span>
			</div>

			<div
				className="flex flex-col gap-0.5 overflow-y-auto p-2"
				data-test={`job-title-rows-${verdict}`}
			>
				{rows.map((row) => (
					<JobTitleRow
						key={row.id}
						isLocked={isLocked}
						isSelected={selectedIds.includes(row.id)}
						moveLabel={moveLabel}
						row={row}
						onDelete={() => onDelete(row.id)}
						onMove={() => onMoveOne(row.id, verdict)}
						onToggleSelect={() => onToggleSelect(row.id, verdict)}
					/>
				))}
			</div>

			<div className="border-t border-gray-200 p-2">
				<input
					className="input-styles w-full rounded-lg px-3 py-2 text-sm"
					data-test={`job-title-add-${verdict}`}
					placeholder={t("leadScoring.jobTitles.addPlaceholder")}
					value={draft}
					onChange={(event) => setDraft(event.target.value)}
					onKeyDown={handleKeyDown}
				/>
			</div>
		</div>
	);
};
